import React from "react";
import { bind } from "decko";
import HlsJS from "hls.js";

import StreamMetadata from "./StreamMetadata";
import CreateNewEvent from "./CreateNewEvent";
import LiveStreamControls from "./LiveStreamControls";
import EventButtonContainer from "./EventButtonContainer";
import MessageLog from "./MessageLog";
import TimeCode from "./TimeCode";
import Util from "./include/Util";
import PossessionTracking from "./PossessionTracking";
import GameLineup from "./include/GameLineup";
import StreamEventTimeline from './StreamEventTimeline';
import GameEvent from './include/GameEvent'
import StreamOptionsDialog from "./StreamOptionsDialog";
import Analytics from "./include/Analytics";
import Config from "./include/Config";

const LiveStreamState = {
    UNDEFINED: "undefined",
    PLAYER: "player",
    CREATING_EVENT: "creating_event",
    OPTIONS: "options_menu",
};

export default class LiveStream extends React.Component {
    constructor (props) {
        super(props);

        this.hlsJsConfig = Util.getHlsJsConfig();
        const segmentSize = 2;
        this.avrgBufferDuration = this.hlsJsConfig.liveSyncDurationCount * segmentSize - (segmentSize/2);
        this.videoElement = null;
        this.hlsjs = null;
        this.eventType = null;
        this.eventTeam = null;
        this.eventCreationTime = null;

        let homeTeamId = this.props.stream.game.home_team.id;
        let visitingTeamId = this.props.stream.game.visiting_team.id;
        let homeTeamColor = localStorage[this.getShirtColorKey(homeTeamId)] || "#ffffff";
        let visitingTeamColor = localStorage[this.getShirtColorKey(visitingTeamId)] || "#ffffff";

        this.state = {
            liveStreamState: LiveStreamState.UNDEFINED,
            paused: true,
            videoDuration: 0,
            videoCurrentTime: 0,
            game: this.props.stream.game,
            streamStart: this.parseTimestamp(this.props.stream.start_timestamp),
            homeTeamGoals: 0,
            visitingTeamGoals: 0,
            livestreamOptions: this.loadOptions(),
            homeTeamKeeper: this.getEmptyKeeper(),
            visitingTeamKeeper: this.getEmptyKeeper(),
            lineup: null,
            messages: [],
            homeTeamColor: homeTeamColor,
            visitingTeamColor: visitingTeamColor,
            bufferRanges: [],
            events: [],
            playersOnPitch: new Set(),
            playersUnavailable: new Set(),
        };
    }

    componentDidMount () {
        console.log("Video element mounted");
        this.hlsjs = new HlsJS(this.hlsJsConfig);

        // Add analytics listeners
        this.hlsjs.on(HlsJS.Events.ERROR, this.onHlsJsError);
        Analytics.applyVideoListeners(this.hlsjs, this.videoElement, this.props.stream);

        this.hlsjs.attachMedia(this.videoElement);
        this.hlsjs.on(HlsJS.Events.MEDIA_ATTACHED, () => {

            console.log("Video element and hls.js are now bound together!");
            this.hlsjs.loadSource(this.buildManifestUrl());
            this.hlsjs.on(HlsJS.Events.MANIFEST_PARSED, () => {

                console.log("Manifest parsed we are starting playback");
                this.videoElement.volume = 0.5;
                this.videoElement.play().catch(() => undefined);
                this.changeLiveStreamState(LiveStreamState.PLAYER);
            });

        });

        //Update the game periodically
        this.updateGameInterval = setInterval(this.reloadGame.bind(this), 2000);

        this.updateEventsInterval = setInterval(this.reloadEvents.bind(this), 15000);
        this.reloadEvents();

        //Update player lineup periodically
        this.updateLineupInterval = setInterval(this.loadLineup.bind(this), 20000);
        this.loadLineup();

        this.timelineTick = setInterval(this.updateBuffer, 500);
    }

    componentWillUnmount () {
        this.hlsjs.destroy();
        clearInterval(this.updateGameInterval);
        clearInterval(this.updateEventsInterval);
        clearInterval(this.updateLineupInterval);
        clearInterval(this.timelineTick);
    }

    getServer () {
        return this.props.stream.server;
    }

    loadOptions () {
        let liveTagging = localStorage[this.getOptionKey("liveTagging")] || "false";
        let extraLiveDelay = parseFloat((localStorage[this.getOptionKey("extraLiveDelay")] || 0));
        return {
            liveTagging: liveTagging === "true",
            extraLiveDelay: Number.isNaN(extraLiveDelay) ? 0 : extraLiveDelay,
        };

    }

    getShirtColorKey (teamId) {
        return "shirt_color_" +
            this.getServer().name + "_" +
            this.props.stream.game.id + "_" +
            teamId;
    }

    getOptionKey (option) {
        return `option_${option}_${this.getServer().name}_${this.props.stream.game.id}`;
    }

    @bind
    onHlsJsError (event, data) {
        if (data.fatal) {
            switch(data.type) {
                case HlsJS.ErrorTypes.NETWORK_ERROR:
                    this.onError("Fatal network error: " + data.details);
                    break;
                case HlsJS.ErrorTypes.MEDIA_ERROR:
                    this.onError("Fatal media error: " + data.details);
                    break;
                default:
                    // cannot recover
                    this.onError("Unrecoverable playback error: " + data.details);
                    break;
            }
        }
    }

    @bind
    onChangeShirtColor (teamId, color) {
        localStorage[this.getShirtColorKey(teamId)] = color;
        if (teamId === this.state.game.home_team.id) {
            this.setState({homeTeamColor: color});
        } else {
            this.setState({visitingTeamColor: color});
        }
    }

    @bind
    onChangeLivestreamOptions (newOptions) {
        localStorage[this.getOptionKey("liveTagging")] = newOptions.liveTagging;
        localStorage[this.getOptionKey("extraLiveDelay")] = newOptions.extraLiveDelay;

        this.setState({livestreamOptions: newOptions});
    }

    @bind
    onError (msg) {
        this.displayLogMsg(msg, true);
    }

    @bind
    displayLogMsg (msg, isError) {
        // Filter away log messages older than 10 seconds
        const now = new Date();
        let messages = this.state.messages.filter((m) => (now - m.timestamp) < (1000 * 10));
        if (msg !== undefined) {
            messages.push({
                msg: msg,
                timestamp: now,
                error: isError
            });
        }
        this.setState({messages: messages});
    }

    @bind
    openOptionsMenu () {
        if (this.state.liveStreamState !== LiveStreamState.PLAYER &&
            this.state.liveStreamState !== LiveStreamState.UNDEFINED) {
            return;
        }
        this.changeLiveStreamState(LiveStreamState.OPTIONS);
    }

    loadLineup () {
        const gameId = this.state.game.id;
        this.getServer().get("/game/" + gameId + "/players", (data) => (
            this.setState({
                lineup: GameLineup.load(data)
            })
        ), (error) => {
            console.error("Problem fetching game lineup: " + error);
        });
    }

    reloadGame () {
        const gameId = this.state.game.id;
        this.getServer().get("/game/" + gameId,
            (data) => {
                this.props.stream.game = data;
                this.setState({
                    game: data,
                });
            },
            (error) => this.onError("Failed to reload game " + gameId + " : "+ error)
        );
    }

    reloadEvents () {
        const gameId = this.state.game.id;
        // TODO: find a better way to load every relevant event from game
        const params = { count: 999, nocache: true, };
        const server = this.getServer();
        const url = server.createUrl('/game/' + gameId + '/events', params);
        server.get(url,
            (data) => {
                this.setState({
                    events: data.events.map((event) => GameEvent.load(event, this.getServer())).filter(e => e !== null)
                });
            },
            (error) => this.onError("Failed to reload events for game " + gameId + ": " + error)
        );
    }

    changeLiveStreamState (state) {
        this.setState({liveStreamState: state});
    }

    buildManifestUrl () {
        return this.getServer().url + "/live/event/" +
            this.props.stream.id + "/Manifest.m3u8";
    }

    @bind
    closeStream () {
        if (this.state.liveStreamState !== LiveStreamState.PLAYER &&
            this.state.liveStreamState !== LiveStreamState.UNDEFINED) {
            return;
        }

        this.props.onCloseStream();
    }

    getCurrentScore () {
        const currentEvents = this.state.events
            .sort((a, b) => a.timestamp - b.timestamp)
            .filter((event) =>
                event.timestamp < this.state.streamStart.getTime() + this.videoElement.currentTime*1000);
        if(currentEvents.length > 0) {
            return currentEvents[currentEvents.length - 1].score;
        }
        return [0, 0];
    }

    getEmptyKeeper () {
        return {
            type: "player",
            id: null,
            value: "",
        };
    }

    getCurrentKeeper(team) {
        if (this.state.lineup === null) {
            return this.getEmptyKeeper();
        }
        const initialKeeperObj = this.state.lineup.getKeeper(team.name);

        let currentKeeper;
        if (initialKeeperObj === null) {
            currentKeeper = {
                type: "player",
                id: null,
                value: "",
            };
        } else {
            currentKeeper = {
                type: "player",
                id: initialKeeperObj.id,
                value: initialKeeperObj.name,
            };
        }

        this.state.events
            .sort((a, b) => a.timestamp - b.timestamp)
            .filter((event) => event.videoOffsetMillisec / 1000 < this.videoElement.currentTime)
            .forEach((event) => {
                if (event.tag.action === "substitution" &&
                    event.tag.team !== undefined &&
                    event.tag["player in"] !== undefined &&
                    event.tag["player out"] !== undefined &&
                    event.tag.team.id === team.id &&
                    event.tag["player out"].id === currentKeeper.id) {

                    currentKeeper = event.tag["player in"];

                } else if ((event.tag.action === "shot" || event.tag.action === "goal") &&
                            event.tag.team !== undefined &&
                            event.tag.team.id !== team.id &&
                            event.tag.keeper !== undefined) {

                        currentKeeper = event.tag.keeper;
                }
        });
        return currentKeeper;
    }

    @bind
    tick () {
        const [playersOnPitch, playersUnavailable] = this.trackPlayersOnPitch();

        const score = this.getCurrentScore();
        this.setState({
            videoDuration: this.videoElement.duration,
            videoCurrentTime: this.videoElement.currentTime,
            homeTeamGoals: score[0],
            visitingTeamGoals: score[1],
            homeTeamKeeper: this.getCurrentKeeper(this.state.game.home_team),
            visitingTeamKeeper: this.getCurrentKeeper(this.state.game.visiting_team),
            playersOnPitch,
            playersUnavailable
        });
    }

    @bind
    trackPlayersOnPitch () {
        const {events, lineup} = this.state;

        let onPitch = new Set();
        let unavailable = new Set();

        if (lineup) {
            lineup.forEach((player) => {
                if (player.position !== "SUB") onPitch.add(player.id);
            });
        }

        const now = this.videoElement.currentTime;
        events.sort((a, b) => a.timestamp - b.timestamp).filter(
            (event) => event.videoOffsetMillisec / 1000 <= now
        ).forEach((event) => {
            if (event.tag.action === "substitution" &&
                event.tag["player in"] !== undefined &&
                event.tag["player out"] !== undefined) {
                onPitch.add(event.tag["player in"].id);
                onPitch.delete(event.tag["player out"].id);
                unavailable.add(event.tag["player out"].id);
            } else if (event.tag.action === "red card" && event.tag["player"] !== undefined) {
                onPitch.delete(event.tag["player"].id);
                unavailable.add(event.tag["player"].id);
            }
        });

        return [onPitch, unavailable];
    }

    @bind
    createEvent (event, team) {

        // Fix an issue where lineup is not yet loaded
        if (this.state.lineup  === null) {
            this.onError("Lineup not yet loaded from server");
            return;
        }

        this.eventType = event;
        this.eventTeam = team;

        if (this.props.stream.is_live && this.state.livestreamOptions.liveTagging) {
            /*
            When live tagging, store absolute timestamp instead.
            Event creation will use the absolute timestamp, and calculate the offset for the video if possible.
             */
            this.eventCreationTime = new Date();
            if (this.state.livestreamOptions.extraLiveDelay && this.state.livestreamOptions.extraLiveDelay > 0) {
                this.eventCreationTime.setSeconds(
                    this.eventCreationTime.getSeconds() - this.state.livestreamOptions.extraLiveDelay
                );
            }
        } else {
            this.eventCreationTime = this.state.videoCurrentTime;
        }
        this.changeLiveStreamState(LiveStreamState.CREATING_EVENT);
    }

    @bind
    closeDialogs () {
        this.changeLiveStreamState(LiveStreamState.PLAYER);
        this.reloadEvents();
    }

    @bind
    seek (time) {
        this.videoElement.currentTime = Math.max(0, time);
        Analytics.streamEvent(this.props.stream, "manualseek", {
            type: "timeline" // User clicked somewhere on the stream timeline
        });
    }

    @bind
    updateBuffer () {
        //Video may have multiple buffer ranges, due to seeking
        const buffers = this.videoElement.buffered;
        let bufferRanges = [];
        for (let i=0; i<buffers.length; ++i) {
            bufferRanges.push({
                start: buffers.start(i),
                end: buffers.end(i),
            });
        }
        this.setState({bufferRanges: bufferRanges});
    }

    parseTimestamp(ts) {
        if(ts) {
            return new Date(ts);
        }
        return undefined;
    }

    render () {
        const server = this.getServer();
        // Unique identifier for the game
        const gameKey = server.name + "_" + this.state.game.id;
        let containerClassname = "col-xs-12";
        if (this.props.quadLayout) {
            containerClassname += " quad-layout";
        }

        if(this.props.displayTimeline) {
            containerClassname += " display-timeline";
        }

        const timelineMargin = this.props.displayTimeline ? 24 : 0;

        const liveStreamContainer = (
            <div className={containerClassname} style={{position: "relative"}}>
                <div className="text-center">
                    <video className="live-stream"
                        ref={(video) => this.videoElement = video}
                        onPlay={() => this.setState({paused: false})}
                        onPause={() => this.setState({paused: true})}
                        onTimeUpdate={this.tick}
                        onDurationChange={this.tick} />
                </div>
                {this.state.liveStreamState !== LiveStreamState.UNDEFINED &&
                    <LiveStreamControls
                        hotkeyHandler={this.props.hotkeyHandler}
                        video={this.videoElement}
                        stream={this.props.stream}
                        paused={this.state.paused}
                        videoCurrentTime={this.state.videoCurrentTime}
                        videoDuration={this.state.videoDuration}
                        avrgBufferDuration={this.avrgBufferDuration}
                        isLive={this.props.stream.is_live}
                        useBottomMargin={this.props.displayTimeline}
                    />
                }
                <div style={{position: "absolute", top: "54px", left: "15px"}}>
                    <EventButtonContainer
                        hotkeyHandler={this.props.hotkeyHandler}
                        side="left"
                        team={this.state.game.home_team}
                        shirtColor={this.state.homeTeamColor}
                        onChangeShirtColor={(color) => this.onChangeShirtColor(this.state.game.home_team.id, color)}
                        gameKey={gameKey}
                        onClicked={this.createEvent}
                        selectKeeper={this.selectKeeper}
                    />
                </div>
                <div style={{position: "absolute", top: "54px", right: "15px"}}>
                    <EventButtonContainer
                        hotkeyHandler={this.props.hotkeyHandler}
                        side="right"
                        team={this.state.game.visiting_team}
                        shirtColor={this.state.visitingTeamColor}
                        onChangeShirtColor={(color) => this.onChangeShirtColor(this.state.game.visiting_team.id, color)}
                        gameKey={gameKey}
                        onClicked={this.createEvent}
                    />
                </div>
                <div style={{position: "absolute", top: "0px", left: "90px"}}>
                    <MessageLog
                        width="350px"
                        height="70px"
                        messages={this.state.messages}
                    />
                </div>
                <TimeCode
                    style={{position: "absolute", width: "106px", bottom: (55 + timelineMargin) + "px", right: "17px"}}
                    streamRecordingTime={this.props.stream.start_timestamp}
                    video={this.videoElement}
                />
                <StreamMetadata style={{position: "absolute", top: "0", right: "15px"}}
                    game={this.state.game}
                    liveModeEnabled={this.state.livestreamOptions.liveTagging}
                    streamRecordingTime={this.props.stream.start_timestamp}
                    videoCurrentTime={Math.floor(this.state.videoCurrentTime*2)/2}
                    onCloseClicked={this.closeStream}
                    onOpenOptionsMenu={this.openOptionsMenu}
                    onGameStateClick={() => this.createEvent("start phase", {})}
                    homeTeamGoals={this.state.homeTeamGoals}
                    visitingTeamGoals={this.state.visitingTeamGoals}
                    homeTeam={this.state.game.home_team.name}
                    visitingTeam={this.state.game.visiting_team.name}
                />
                {this.props.possessionTracking &&
                    <PossessionTracking
                        hotkeyHandler={this.props.hotkeyHandler}
                        videoCurrentTime={this.state.videoCurrentTime}
                        streamRecordingTime={this.props.stream.start_timestamp}
                        homeTeamColor={this.state.homeTeamColor}
                        visitingTeamColor={this.state.visitingTeamColor}
                        quadStreamIdx={this.props.quadStreamIdx}
                        server={server}
                        logger={this.displayLogMsg}
                        onError={this.onError}
                        game={this.state.game}
                    />
                }
                {this.state.liveStreamState === LiveStreamState.CREATING_EVENT &&
                    <CreateNewEvent
                        hotkeyHandler={this.props.hotkeyHandler}
                        videoCurrentTime={this.state.videoCurrentTime}
                        eventCreationTime={this.eventCreationTime}
                        game={this.state.game}
                        stream={this.props.stream}
                        homeTeam={this.state.game.home_team}
                        visitingTeam={this.state.game.visiting_team}
                        homeTeamGoals={this.state.homeTeamGoals}
                        visitingTeamGoals={this.state.visitingTeamGoals}
                        homeTeamKeeper={this.state.homeTeamKeeper}
                        visitingTeamKeeper={this.state.visitingTeamKeeper}
                        liveModeEnabled={this.state.livestreamOptions.liveTagging}
                        eventType={this.eventType}
                        selectedTeam={this.eventTeam}
                        existingEvents={this.state.events}
                        lineup={this.state.lineup}
                        playersOnPitch={this.state.playersOnPitch}
                        playersUnavailable={this.state.playersUnavailable}
                        server={server}
                        logger={this.displayLogMsg}
                        onError={this.onError}
                        onDone={this.closeDialogs}
                    />
                }
                {this.state.liveStreamState === LiveStreamState.OPTIONS &&
                    <StreamOptionsDialog
                        hotkeyHandler={this.props.hotkeyHandler}
                        game={this.state.game}
                        options={this.state.livestreamOptions}
                        onChangeOptions={this.onChangeLivestreamOptions}
                        onError={this.onError}
                        onDone={()=>this.changeLiveStreamState(LiveStreamState.PLAYER)}
                    />
                }
                {this.props.displayTimeline && (
                    <StreamEventTimeline
                        onSeek={this.seek}
                        streamStart={this.state.streamStart}
                        gameStart={this.parseTimestamp(this.props.stream.game.start_time)}
                        gameFinished={this.parseTimestamp(this.props.stream.game.finished_time)}
                        duration={this.state.videoDuration}
                        progress={this.state.videoCurrentTime}
                        video={this.videoElement}
                        bufferRanges={this.state.bufferRanges}
                        events={this.state.events}
                        homeTeam={this.props.stream.game.home_team}
                        homeTeamColor={this.state.homeTeamColor}
                        visitingTeam={this.props.stream.game.visiting_team}
                        visitingTeamColor={this.state.visitingTeamColor}
                    />
                )}
            </div>
        );

        return (
            <div className="row">{liveStreamContainer}</div>
        );
    }
}

LiveStream.defaultProps = {
};



// WEBPACK FOOTER //
// ./src/js/LiveStream.js